import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/themeContext";
import { requestOtpAPI, submitOtpAPI } from "../lib/api/api";
import { toast } from "react-toastify";
import { fetchCheckAuthAPI } from "../redux/slices/authSlice";

const LoginComponent = () => {
  const loginData = useSelector((state) => state.login.data);

  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showOtpFlow, setShowOtpFlow] = useState(false);
  const [otpStep, setOtpStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);

  const impressumLink = "https://www.edgeecom.de/legal/imprint";
  const datenschutzLink = "https://www.edgeecom.de/legal/privacy-policy";
  const agbLink = "https://www.edgeecom.de/legal/terms-of-service";

  const imageSrc =
    theme === "dark" ? "/static/bg-flare-dark.svg" : "/static/bg-flare-light.svg";

  const startOtpFlow = () => {
    setShowOtpFlow(true);
    setOtpStep(1);
    setOtpCode("");
    setOtpRequested(false);
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    if (!identifier.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein.");
      return;
    }

    setIsRequestingOtp(true);
    try {
      await requestOtpAPI(identifier.trim());
      toast.success("OTP wurde an deine E-Mail gesendet.");
      setOtpRequested(true);
      setOtpStep(2);
    } catch (error) {
      setOtpRequested(false);
      setOtpStep(1);
      // Fehler wird bereits im API-Layer getoastet.
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const resetOtpFlowState = (clearIdentifier = true) => {
    setShowOtpFlow(false);
    setOtpStep(1);
    setOtpCode("");
    setOtpRequested(false);
    if (clearIdentifier) {
      setIdentifier("");
    }
  };

  const handleSubmitOtp = async (event) => {
    event.preventDefault();
    const cleanedCode = otpCode.replace(/\D/g, "");

    if (!identifier.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein.");
      return;
    }

    if (cleanedCode.length !== 6) {
      toast.error("Bitte gib den 6-stelligen OTP-Code ein.");
      return;
    }

    setIsSubmittingOtp(true);
    try {
      const response = await submitOtpAPI({
        identifier: identifier.trim(),
        code: cleanedCode,
      });

      const responseStatus = String(response?.status || "").toLowerCase();
      const isAuthorized =
        responseStatus === "authorized" || responseStatus === "success";

      if (isAuthorized) {
        toast.success("Erfolgreich angemeldet.");
        resetOtpFlowState();
        try {
          const authResponse = await dispatch(fetchCheckAuthAPI()).unwrap();
          if (authResponse?.status === "authorized") {
            navigate("/home", { replace: true });
          } else {
            navigate("/login", { replace: true });
          }
        } catch {
          navigate("/login", { replace: true });
        }
        return;
      }

      if (responseStatus === "unauthorized") {
        toast.error(
          response?.message ||
            "OTP konnte nicht bestätigt werden. Bitte überprüfe den Code und versuche es erneut."
        );
        setOtpCode("");
        return;
      }

      if (response?.message) {
        toast.info(response.message);
      } else {
        toast.info("OTP bestätigt. Du kannst dich jetzt anmelden.");
      }
    } catch (error) {
      // Fehler wird bereits im API-Layer getoastet.
    } finally {
      setIsSubmittingOtp(false);
    }
  };

  const otpHeading = otpStep === 1 ? "OTP anfordern" : "OTP bestätigen";
  const otpDescription =
    otpStep === 1
      ? "Wir senden dir einen einmaligen Code per E-Mail. Bitte gib deine Adresse an."
      : "Gib den sechsstelligen Code ein, den wir dir gerade gesendet haben.";

  const handleBackToMethods = () => {
    resetOtpFlowState();
  };

  return (
    <div
      style={{
        backgroundImage: `url(${imageSrc})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundPosition: "center"
      }}
    >
      <div className="teal-effect"></div>
      <div className="blue-effect"></div>

      <div className="flex h-screen text-textTitle flex-col items-center gap-12 justify-center">
        <div className="flex border-[1px] border-borderBottom rounded-lg flex-col items-center gap-12 justify-center px-10 shadow pb-4 pt-20">
          <img
            src={
              theme === "dark"
                ? "/static/darkmode-logo.svg"
                : "/static/lightmode-logo.svg"
            }
            alt="logo"
            className="w-full"
          />

          {!showOtpFlow && (
            <>
              <div className="text-left">
                <p className="text-[22px] tracking-wider text-primary">Anmelden</p>
                <p className="text-[14px] text-textPrimary tracking-wider w-[260px] font-light">
                  Für deinen ersten Login bitte unbedingt die OTP-Anmeldung verwenden.
                </p>
              </div>

              <div className="flex w-full max-w-[18rem] flex-col gap-3 mx-auto">
                <Link to={loginData?.login_url || "#"} className="w-full">
                  <button
                    className="bg-gradient-to-br from-primary to-main text-[16px] cursor-pointer text-text w-full py-3 font-medium rounded disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={!loginData?.login_url}
                  >
                    Mit Discord anmelden
                  </button>
                </Link>

                <button
                  onClick={startOtpFlow}
                  className="bg-transparent border border-borderBottom text-[16px] cursor-pointer text-textPrimary w-full py-3 font-medium rounded transition hover:border-primary hover:text-primary"
                >
                  Mit OTP anmelden
                </button>
              </div>
            </>
          )}

          {showOtpFlow && (
            <div className="w-full max-w-[18rem] mx-auto rounded border border-borderBottom bg-transparent p-4 shadow-sm">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[16px] font-semibold tracking-wide text-primary">
                    {otpHeading}
                  </p>
                  <p className="mt-1 text-[12px] leading-5 text-textPrimary">
                    {otpDescription}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleBackToMethods}
                  className="text-[12px] text-textsecondary transition hover:text-primary"
                >
                  Abbrechen
                </button>
              </div>
              {otpStep === 1 ? (
                <form
                  onSubmit={handleRequestOtp}
                  className="flex w-full flex-col gap-3"
                >
                  <div>
                    <label
                      htmlFor="otp-identifier"
                      className="block text-[12px] font-medium text-textPrimary tracking-wider"
                    >
                      E-Mail-Adresse
                    </label>
                    <input
                      id="otp-identifier"
                      type="email"
                      value={identifier}
                      onChange={(event) => {
                        setIdentifier(event.target.value);
                        setOtpRequested(false);
                        setOtpStep(1);
                        setOtpCode("");
                      }}
                      className="mt-2 w-full rounded border border-borderBottom bg-transparent px-3 py-2 text-[14px] text-textPrimary focus:border-primary focus:outline-none"
                      placeholder="name@example.com"
                      autoComplete="email"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="bg-gradient-to-br from-primary to-main text-[14px] cursor-pointer text-text w-full p-2 font-medium rounded disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={isRequestingOtp}
                  >
                    {isRequestingOtp ? "Sende..." : "OTP senden"}
                  </button>
                </form>
              ) : (
                <form
                  onSubmit={handleSubmitOtp}
                  className="flex w-full flex-col gap-3"
                >
                  <div>
                    <label
                      htmlFor="otp-code"
                      className="block text-[12px] font-medium text-textPrimary tracking-wider"
                    >
                      6-stelliger OTP-Code
                    </label>
                    <input
                      id="otp-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(event) =>
                        setOtpCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      className="mt-2 w-full rounded border border-borderBottom bg-transparent px-3 py-2 text-[14px] text-textPrimary focus:border-primary focus:outline-none"
                      placeholder="••••••"
                      autoComplete="one-time-code"
                      required
                    />
                    {otpRequested && (
                      <p className="mt-2 text-[12px] text-green-500">
                        Wir haben dir einen Code zugesendet. Bitte prüfe deinen Posteingang.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="bg-gradient-to-br from-primary to-main text-[16px] cursor-pointer text-text w-full p-2 font-medium rounded disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={isSubmittingOtp}
                  >
                    {isSubmittingOtp ? "Prüfe..." : "OTP bestätigen"}
                  </button>
                </form>
              )}
            </div>
          )}
          <div className="divide-x divide-textsecondary">
            <a
              target="_blank"
              href={impressumLink}
              className="text-textsecondary pr-3 text-[12px] tracking-wider"
              rel="noreferrer"
            >
              Impressum
            </a>
            <a
              target="_blank"
              href={agbLink}
              className="text-textsecondary px-3 text-[12px] tracking-wider"
              rel="noreferrer"
            >
              AGB
            </a>
            <a
              target="_blank"
              href={datenschutzLink}
              className="text-textsecondary pl-3 text-[12px] tracking-wider"
              rel="noreferrer"
            >
              Datenschutz
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;
